import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit as EditIcon, Check as CheckIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { Method } from "./types";

interface MethodsSectionProps {
  availableMethods: Method[];
  methodsUsed: string[];
  onToggleMethod: (method: Method) => void;
}

const MethodsSection: React.FC<MethodsSectionProps> = ({
  availableMethods,
  methodsUsed,
  onToggleMethod,
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const isMethodActive = (method: Method): boolean => {
    const key = method.id || method.name;
    return key
      ? methodsUsed.includes(key) ||
          (!!method.name && methodsUsed.includes(method.name))
      : false;
  };

  return (
    <Paper sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
            mb: 0,
            textTransform: "uppercase"
          }}>
          {t("coachingNavigator.clientOverview.methodsUsed")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => setDialogOpen(true)}
        >
          {t("coachingNavigator.clientOverview.editMethods")}
        </Button>
      </Box>
      <Box
        sx={{
          maxHeight: 140,
          overflow: "auto",
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {availableMethods.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic"
            }}>
            {t("coachingNavigator.clientOverview.noMethodsDefinedYet")}
          </Typography>
        ) : (
          availableMethods.map((method) => {
            const key = method.id || method.name;
            const isActive = isMethodActive(method);
            return (
              <Chip
                key={key}
                label={method.name}
                icon={isActive ? <CheckIcon fontSize="small" /> : undefined}
                color={isActive ? "primary" : "default"}
                variant={isActive ? "filled" : "outlined"}
                size="small"
              />
            );
          })
        )}
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth slotProps={{ paper: {
          sx: { bgcolor: "#fff" },
        } }}
      >
        <DialogTitle>
          {t("coachingNavigator.clientOverview.editMethods")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pt: 2 }}>
            {availableMethods.map((method) => {
              const key = method.id || method.name;
              const isActive = isMethodActive(method);
              return (
                <Chip
                  key={key}
                  label={method.name}
                  icon={isActive ? <CheckIcon fontSize="small" /> : undefined}
                  color={isActive ? "primary" : "default"}
                  variant={isActive ? "filled" : "outlined"}
                  onClick={() => onToggleMethod(method)}
                  sx={{ cursor: "pointer" }}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined">
            {t("coachingNavigator.clientOverview.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MethodsSection;
